import React, { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Edit3, X, Save, FileText, Eye, Paperclip, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { sanitizeHtml } from "../lib/sanitize";

const AVAILABLE_VARS = [
  ["company_name", "Lead company"],
  ["client_poc_name", "POC name"],
  ["client_poc_email", "POC email"],
  ["lead_stage", "Stage"],
  ["lead_source", "Source"],
  ["rep_name", "Your name (sender)"],
  ["rep_email", "Your email"],
  ["rep_phone", "Your phone"],
  ["today", "Today's date"],
];

function defaultForm() {
  return { name: "", subject: "", body: "", description: "", is_active: true };
}

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | template id
  const [form, setForm] = useState(defaultForm());
  const [preview, setPreview] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const loadAttachments = async (templateId) => {
    try {
      const { data } = await api.get(`/email-templates/${templateId}/attachments`);
      setAttachments(data);
    } catch {
      setAttachments([]);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/email-templates");
      setTemplates(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(defaultForm());
    setAttachments([]);
    setEditing("new");
    setPreview(false);
  };

  const openEdit = (t) => {
    setForm({ name: t.name, subject: t.subject, body: t.body, description: t.description || "", is_active: t.is_active !== false });
    setEditing(t.id);
    setPreview(false);
    loadAttachments(t.id);
  };

  const closeEditor = () => setEditing(null);

  const insertVar = (key) => {
    setForm((f) => ({ ...f, body: (f.body || "") + ` {{${key}}}` }));
  };

  const onPickFile = () => fileInputRef.current?.click();

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-selected
    if (!file || editing === "new") return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large — 25 MB per-template cap");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/email-templates/${editing}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Attached ${file.name}`);
      loadAttachments(editing);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (att) => {
    if (!window.confirm(`Remove "${att.filename}" from this template?`)) return;
    try {
      await api.delete(`/email-templates/${editing}/attachments/${att.id}`);
      toast.success("Removed");
      loadAttachments(editing);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const fmtBytes = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
      toast.error("Name, subject, and body are required");
      return;
    }
    try {
      if (editing === "new") {
        await api.post("/email-templates", form);
        toast.success("Template created");
      } else {
        await api.patch(`/email-templates/${editing}`, form);
        toast.success("Template updated");
      }
      closeEditor();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/email-templates/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Layout title="Email Templates" breadcrumb={["Admin", "Email Templates"]}>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-600">
              Reusable email templates with variable substitution. Use{" "}
              <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 text-xs">{`{{variable}}`}</code>{" "}
              placeholders — they're filled in from the lead, the sender, and today's date when sent.
            </p>
          </div>
          <button
            data-testid="template-new-btn"
            onClick={openNew}
            className="px-3 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium inline-flex items-center gap-1.5"
          >
            <Plus size={14} /> New template
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          {templates.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="templates-empty">
              <FileText size={28} className="mx-auto mb-2 text-slate-300" />
              No templates yet. Create your first one to start sending personalized emails.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-2.5">Name</th>
                  <th className="px-5 py-2.5">Subject</th>
                  <th className="px-5 py-2.5">Status</th>
                  <th className="px-5 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id} data-testid={`template-row-${t.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-5 py-3 font-medium text-slate-900">{t.name}</td>
                    <td className="px-5 py-3 text-slate-700 max-w-md truncate">{t.subject}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${
                        t.is_active
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-slate-100 text-slate-500 border-slate-200"
                      }`}>{t.is_active ? "Active" : "Inactive"}</span>
                    </td>
                    <td className="px-5 py-3 text-right space-x-1">
                      <button
                        data-testid={`template-edit-${t.id}`}
                        onClick={() => openEdit(t)}
                        className="text-xs text-slate-600 hover:text-orange-600 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100"
                      >
                        <Edit3 size={12} /> Edit
                      </button>
                      <button
                        data-testid={`template-delete-${t.id}`}
                        onClick={() => remove(t)}
                        className="text-xs text-red-600 hover:text-red-700 inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-red-50"
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeEditor} data-testid="template-editor-dialog">
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          >
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">
                {editing === "new" ? "New template" : "Edit template"}
              </h3>
              <button type="button" onClick={closeEditor} className="p-1.5 rounded-md hover:bg-slate-100">
                <X size={16} />
              </button>
            </div>
            <div className="p-6 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Name *</label>
                  <input data-testid="template-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="tpl-input" placeholder="e.g. Cold outreach #1" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Status</label>
                  <select value={form.is_active ? "1" : "0"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })} className="tpl-input">
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Subject *</label>
                <input data-testid="template-form-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="tpl-input" placeholder="Quick intro — {{company_name}}" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Description</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="tpl-input" placeholder="(optional internal note)" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Body (HTML) *</label>
                  <button type="button" onClick={() => setPreview((v) => !v)} className="text-xs text-orange-600 inline-flex items-center gap-1" data-testid="template-preview-toggle">
                    <Eye size={12} /> {preview ? "Edit" : "Preview"}
                  </button>
                </div>
                {preview ? (
                  <div
                    data-testid="template-preview"
                    className="border border-slate-200 rounded-md p-4 bg-slate-50 min-h-[180px] prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(form.body) }}
                  />
                ) : (
                  <textarea
                    data-testid="template-form-body"
                    value={form.body}
                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                    rows={10}
                    className="tpl-input font-mono text-xs"
                    placeholder="<p>Hi {{client_poc_name}},</p>"
                  />
                )}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="template-attachments-block">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5">
                    <Paperclip size={11} /> Attachments
                    {attachments.length > 0 && (
                      <span className="normal-case tracking-normal text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200">
                        {attachments.length}/5
                      </span>
                    )}
                  </div>
                  {editing !== "new" && (
                    <button
                      type="button"
                      onClick={onPickFile}
                      disabled={uploading || attachments.length >= 5}
                      data-testid="template-attach-btn"
                      className="text-xs px-2.5 py-1 rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white inline-flex items-center gap-1"
                    >
                      <Upload size={11} /> {uploading ? "Uploading…" : "Attach file"}
                    </button>
                  )}
                </div>
                <input type="file" ref={fileInputRef} onChange={uploadFile} className="hidden" data-testid="template-attach-file-input" />
                {editing === "new" ? (
                  <div className="text-xs text-slate-500 italic">Save the template first to start attaching files.</div>
                ) : attachments.length === 0 ? (
                  <div className="text-xs text-slate-500 italic">No attachments. Max 5 files, 25 MB total.</div>
                ) : (
                  <ul className="space-y-1.5">
                    {attachments.map((a) => (
                      <li key={a.id} data-testid={`template-attachment-${a.id}`} className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded-md px-2.5 py-1.5">
                        <FileText size={12} className="text-orange-600 shrink-0" />
                        <span className="flex-1 truncate text-slate-800">{a.filename}</span>
                        <span className="text-slate-500 text-[10px] shrink-0">{fmtBytes(a.size)}</span>
                        <a
                          href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/email-templates/attachments/${a.id}/download`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-slate-500 hover:text-orange-600 shrink-0"
                          title="Download"
                          data-testid={`template-attachment-download-${a.id}`}
                        >
                          <Download size={12} />
                        </a>
                        <button
                          type="button"
                          onClick={() => removeAttachment(a)}
                          className="text-red-500 hover:text-red-600 shrink-0"
                          title="Remove"
                          data-testid={`template-attachment-remove-${a.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Available variables — click to insert</div>
                <div className="flex flex-wrap gap-1.5">
                  {AVAILABLE_VARS.map(([key, label]) => (
                    <button
                      type="button"
                      key={key}
                      onClick={() => insertVar(key)}
                      data-testid={`template-var-${key}`}
                      className="text-xs px-2 py-1 rounded-full border border-slate-200 bg-white hover:border-orange-300 hover:bg-orange-50 text-slate-700"
                    >
                      <code>{`{{${key}}}`}</code> <span className="text-slate-400">— {label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <style>{`.tpl-input{width:100%;padding:.5rem .65rem;border:1px solid rgb(226 232 240);border-radius:.375rem;font-size:.85rem;background:white;outline:none}.tpl-input:focus{border-color:rgb(249 115 22);box-shadow:0 0 0 2px rgba(249,115,22,.2)}`}</style>
            <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2 bg-slate-50/50">
              <button type="button" onClick={closeEditor} className="px-4 py-2 text-sm rounded-md border border-slate-200 bg-white">Cancel</button>
              <button type="submit" data-testid="template-form-submit" className="px-4 py-2 text-sm rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium inline-flex items-center gap-1.5">
                <Save size={13} /> {editing === "new" ? "Create" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
